import React, { useState, useCallback, useEffect } from 'react';
import { FormField, NumberInput, InputAdornment, Chips } from '@cian/ui-kit';
import { useFormContext } from 'react-hook-form';

import styles from './styles.css';
import { type IField, type IFormState } from '../../types/calculator';
import { useBaseField } from '../BaseField';
import { getFieldPlaceholder } from '../../utils/getFieldPlaceholder';

export const InputWithOptions: React.FC<IField> = props => {
  const { calculateForField, calculateForChips, source } = props;
  const {
    field: { onChange, name, value: fieldValue, ref },
    fieldState,
  } = useBaseField(props);
  const { watch } = useFormContext<IFormState>();
  const formState = watch();
  const [value, setValue] = useState<number | undefined>(undefined);
  const [chipValue, setChipValue] = useState<string | null>(null);

  const handleChangeOption = useCallback(
    (value: string) => {
      const resultValueForInput = calculateForField ? calculateForField(Number(value), formState) : Number(value);

      setValue(resultValueForInput);
      onChange(resultValueForInput);
    },
    [calculateForField, formState, onChange],
  );

  useEffect(() => {
    if (!fieldValue) {
      setValue(undefined);

      return;
    }

    const inputValue = Number(fieldValue);

    if (inputValue || inputValue === 0) {
      setValue(inputValue);
      if (calculateForChips) {
        setChipValue(calculateForChips(inputValue, formState).toString());
      } else {
        setChipValue(inputValue.toString());
      }
    }
  }, [fieldValue]);

  const handleChangeInput = useCallback(
    async (inputValue: number | undefined) => {
      const value = inputValue ? inputValue : undefined;

      setValue(value);
      await onChange(value);
    },
    [onChange],
  );

  if (!source) {
    return null;
  }

  const isError = fieldState.isDirty || fieldState.invalid;
  const isAllChipsHidden = source.filter(({ hidden }) => hidden).length === source.length;

  const options = source.map(option => {
    return {
      value: option.value,
      label: option.label,
      disabled: option.disabled,
    };
  });

  return (
    <>
      <FormField
        label={props.label}
        errorMessage={isError ? fieldState.error : undefined}
        required={!!props.validation?.required}
        withoutMargin
      >
        <NumberInput
          data-testid={name}
          value={Number(value)}
          onChange={handleChangeInput}
          placeholder={getFieldPlaceholder(props.placeholder)}
          invalid={Boolean(fieldState.isDirty && fieldState.error)}
          disabled={props.disabled}
          name={name}
          ref={ref}
          rightAdornment={<InputAdornment>{props.rightAdornment}</InputAdornment>}
          decimalDigits={props.decimalDigits}
          size="M"
          min={0}
        />
      </FormField>
      {!isAllChipsHidden && (
        <div className={styles.chips}>
          <Chips options={options} value={[chipValue]} onChipClick={handleChangeOption} />
        </div>
      )}
    </>
  );
};

InputWithOptions.displayName = 'InputWithOptions';
